import MainSideBar from "@/components/layout/navigation/sidebar";
import { useMenu } from "@/hooks/useMenu";
import { cn } from "@/utils/css";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useVenue } from "@/hooks/useVenue";
import VenueModal from "@/components/layout/navigation/VenueModal";
import CommandPalette from "@/components/layout/navigation/CommandPalette/Components/CommandPalette";

const SIDEBAR_WIDTH_TRANSITION =
  "md:transition-[width] md:duration-300 md:ease-in-out";
const SIDEBAR_PADDING_TRANSITION =
  "md:transition-[padding] md:duration-300 md:ease-in-out";

const COLLAPSED_SIDEBAR_ROUTES = ["/past-orders"];

const isCollapsedSidebarRoute = (pathname: string): boolean =>
  COLLAPSED_SIDEBAR_ROUTES.some((route) => pathname.startsWith(route));

export default function MainLayout({ organisation, children }) {
  const router = useRouter();
  const [collapseSidebar, setCollapseSidebarState] = useState(() =>
    isCollapsedSidebarRoute(router.pathname),
  );
  const [animateSidebar, setAnimateSidebar] = useState(false);
  const { menuOpen } = useMenu();
  const [selectedLink, setSelectedLink] = useState("overview");
  const animateTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { venues, selectedVenue, setSelectedVenue } = useVenue();
  const [isVenueModalOpen, setIsVenueModalOpen] = useState(false);
  const onToggleVenueModal = () => setIsVenueModalOpen((prev) => !prev);

  const setCollapseSidebar = useCallback((value: boolean) => {
    setAnimateSidebar(true);
    setCollapseSidebarState(value);
  }, []);

  useEffect(() => {
    if (!animateSidebar) {
      return;
    }
    if (animateTimeout.current) {
      clearTimeout(animateTimeout.current);
    }
    animateTimeout.current = setTimeout(() => setAnimateSidebar(false), 350);
    return () => {
      if (animateTimeout.current) {
        clearTimeout(animateTimeout.current);
      }
    };
  }, [animateSidebar]);

  useEffect(() => {
    // TODO: Add Links Back in
    const links = {
      // "/": "overview",
      "/accounts": "accounts",
      "/inventory": "inventory",
      "/past-orders": "past-orders",
      "/devices": "devices",
      "/reports": "reports",
      "/redeemables": "redeemables",
      "/settings": "settings",
      "/support": "support",
    };

    const path = router.pathname;
    const baseRoute = `/${path.split("/")[1]}`;
    setSelectedLink(links[baseRoute] || "overview");
  }, [router.pathname]);

  useEffect(() => {
    if (isCollapsedSidebarRoute(router.pathname)) {
      setCollapseSidebarState(true);
    }
  }, [router.pathname]);

  const sidebarWidthTransition = animateSidebar ? SIDEBAR_WIDTH_TRANSITION : "";
  const sidebarPaddingTransition = animateSidebar
    ? SIDEBAR_PADDING_TRANSITION
    : "";

  return (
    <div className={cn("w-full", animateSidebar && "overflow-x-clip")}>
      <CommandPalette />
      {isVenueModalOpen && (
        <VenueModal
          onToggleModal={onToggleVenueModal}
          venues={venues}
          selectedVenue={venues.find((venue) => venue.id === selectedVenue)}
          setSelectedVenue={setSelectedVenue}
        />
      )}
      <div className="flex">
        <div
          id="main-sidebar-container"
          className={cn(
            "h-full overflow-hidden bg-sidebar-gradient md:block",
            sidebarWidthTransition,
            menuOpen
              ? cn(
                  "fixed left-0 z-20 block w-full md:block",
                  collapseSidebar ? "md:w-24" : "md:w-64",
                )
              : cn(
                  "hidden md:block",
                  collapseSidebar ? "w-24" : "w-64",
                  "fixed top-0 right-0 bottom-0 left-0 z-20",
                ),
          )}
        >
          <MainSideBar
            organisation={organisation}
            selected={selectedLink}
            setCollapseSidebar={setCollapseSidebar}
            collapseSidebar={collapseSidebar}
            onToggleVenueModal={onToggleVenueModal}
          />
        </div>
        <div
          className={cn(
            "w-full bg-dines-purple-light",
            sidebarPaddingTransition,
            collapseSidebar ? "md:pl-24" : "md:pl-64",
            menuOpen && "hidden",
          )}
          style={{
            boxShadow: "-1px 0 #e3e8ee",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
